import { searchKarkhanaWorkers } from "../../../services/karkhanaApi";
import type { Person } from "../rounds/PersonPicker";

/**
 * The studio's workers, kept on the phone so arrivals can be marked without signal. Refreshed
 * whenever the attendance page opens online.
 */
type Storage = Pick<globalThis.Storage, "getItem" | "setItem">;

const key = (orgId: string) => `hkp.karkhana.roster.${orgId}`;
const PAGE = 200;
const MAX_PAGES = 15;

export const readRoster = (orgId: string, store: Storage = localStorage): Person[] => {
  try {
    const v = JSON.parse(store.getItem(key(orgId)) || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

export const refreshRoster = async (orgId: string, store: Storage = localStorage): Promise<Person[]> => {
  const all: Person[] = [];
  for (let page = 0; page < MAX_PAGES; page++) {
    // The worker list counts offset in pages, not rows.
    const res: any = await searchKarkhanaWorkers({ offset: page, limit: PAGE });
    if (!res?.success) return readRoster(orgId, store);
    const docs: any[] = res.data?.docs || [];
    docs.forEach((w) => all.push({ id: w.id, code: w.workerCode, name: w.name }));
    if (docs.length < PAGE) break;
  }
  try {
    store.setItem(key(orgId), JSON.stringify(all));
  } catch {
    // Storage full or blocked: search falls back to the server.
  }
  return all;
};

/**
 * Asks the server when the phone's copy has no match: someone added on another phone since the
 * last refresh. Empty when offline or the server fails, so the caller still offers "Add".
 */
export const ONLINE_SEARCH_MS = 3000;

export const searchWorkersOnline = async (q: string, limit = 8, waitMs = ONLINE_SEARCH_MS): Promise<Person[]> => {
  if (!q.trim() || (typeof navigator !== "undefined" && navigator.onLine === false)) return [];
  let res: any = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    // One bar of signal can hang a request for a minute: give up and let them add the person.
    res = await Promise.race([
      searchKarkhanaWorkers({ q: q.trim(), limit }),
      new Promise((resolve) => {
        timer = setTimeout(() => resolve(null), waitMs);
      }),
    ]);
  } catch {
    // Treated as no match.
  } finally {
    clearTimeout(timer);
  }
  return res?.success ? (res.data?.docs || []).map((w: any) => ({ id: w.id, code: w.workerCode, name: w.name })) : [];
};

const norm = (s: string) => s.toUpperCase().replace(/[\s_-]/g, "");

/** Codes lead (J12 finds J_12 and J_120 first), then names containing the text. */
export const matchRoster = (roster: Person[], q: string, limit = 8): Person[] => {
  const t = norm(q.trim());
  if (!t) return [];
  const byCode = roster.filter((p) => norm(p.code || "").startsWith(t));
  const byName = roster.filter((p) => !byCode.includes(p) && (p.name || "").toUpperCase().includes(q.trim().toUpperCase()));
  return [...byCode, ...byName].slice(0, limit);
};
